import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { parseJsonObject } from '../../utils/parseJsonObject'

// Sortable / searchable / paginated table for knowledge-graph nodes; columns shape defined in PropTypes.
function SortIcon({ active, order }) {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" />
  return order === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
}
SortIcon.propTypes = { active: PropTypes.bool.isRequired, order: PropTypes.string.isRequired }

// Tolerates a stored primitive/array/null (e.g. a stray "null"/"42") so saved.sortBy etc. can be read
// safely. Shared with KnowledgeGraphTab.loadGridConfig via parseJsonObject.
const loadCfg = (key) => parseJsonObject(key && localStorage.getItem(key))

export default function KnowledgeNodeTable({
  rows,
  columns,
  onRowClick,
  rowKey,
  searchKeys = [],
  storageKey,
  pageSize = 20,
  searchPlaceholder = 'Search…',
  emptyText = 'No matching nodes.',
  fillHeight = false
}) {
  // Parse the saved config exactly once at mount (stored in state so it never recomputes). A memo on
  // storageKey would recompute on a storageKey change without the derived state following, which is
  // misleading; this reads the key a single time.
  const [saved] = useState(() => loadCfg(storageKey))
  const [sortBy, setSortBy] = useState(saved.sortBy || columns[0]?.key || null)
  const [sortOrder, setSortOrder] = useState(saved.sortOrder || 'asc')
  const [search, setSearch] = useState(saved.search || '')
  const [debounced, setDebounced] = useState(saved.search || '')
  const [page, setPage] = useState(1)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    if (!storageKey) return
    try {
      // Only persist `search` when the search box is actually shown; otherwise a caller without
      // `searchKeys` would store (and later restore) state that can never filter anything.
      localStorage.setItem(
        storageKey,
        JSON.stringify({ sortBy, sortOrder, ...(searchKeys.length ? { search } : {}) })
      )
    } catch {
      /* ignore */
    }
    // Depend on searchKeys.length (a primitive), not the array: callers that omit `searchKeys` pass a
    // fresh `[]` each render, which would otherwise re-fire this effect (and rewrite localStorage) on
    // every unrelated re-render.
  }, [storageKey, sortBy, sortOrder, search, searchKeys.length])

  const colByKey = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c])), [columns])

  const filtered = useMemo(() => {
    const q = debounced.trim().toLowerCase()
    if (!q || searchKeys.length === 0) return rows
    return rows.filter((r) =>
      searchKeys.some((k) =>
        String(r[k] ?? '')
          .toLowerCase()
          .includes(q)
      )
    )
  }, [rows, debounced, searchKeys])

  const sorted = useMemo(() => {
    if (!sortBy) return filtered
    // Inlined here (rather than a component-body helper) so the memo's dependencies are complete.
    const sortValue = (row) => {
      const col = colByKey[sortBy]
      if (!col) return ''
      if (col.sortValue) return col.sortValue(row)
      const v = row[sortBy]
      return v == null ? '' : v
    }
    const col = colByKey[sortBy]
    const out = filtered.slice().sort((a, b) => {
      // Opt-in per-column pin: rows flagged by alwaysLast() sink to the bottom regardless of the
      // asc/desc direction (e.g. "not visited" nodes, which mean "no data" rather than "worst").
      if (col?.alwaysLast) {
        const al = col.alwaysLast(a)
        const bl = col.alwaysLast(b)
        if (al !== bl) return al ? 1 : -1
      }
      const av = sortValue(a)
      const bv = sortValue(b)
      const cmp =
        typeof av === 'string' || typeof bv === 'string'
          ? String(av).localeCompare(String(bv))
          : (av ?? 0) - (bv ?? 0)
      return sortOrder === 'asc' ? cmp : -cmp
    })
    return out
  }, [filtered, sortBy, sortOrder, colByKey])

  const total = sorted.length

  // Reset to page 1 whenever the result set changes shape. Depend on the result count (a primitive),
  // not the `rows` array reference, so a new-but-equal array from the parent doesn't reset the page.
  // sortBy/sortOrder are intentional deps too (not redundant): a re-sort must return to page 1 even
  // though it doesn't change `total`.
  useEffect(() => setPage(1), [debounced, sortBy, sortOrder, total])
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.min(page, totalPages)
  const start = (safePage - 1) * pageSize
  const pageRows = sorted.slice(start, start + pageSize)

  const handleSort = (col) => {
    if (col.sortable === false) return
    if (sortBy === col.key) {
      setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(col.key)
      setSortOrder('asc')
    }
  }

  return (
    // fillHeight: become a flex column that fills the parent so the table body scrolls internally
    // (page stays viewport-height) instead of stretching the page to its natural height.
    <div className={fillHeight ? 'h-full flex flex-col min-h-0' : ''}>
      {searchKeys.length > 0 && (
        <div className="relative mb-3 max-w-xs shrink-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
      )}

      <div
        className={`border border-gray-200 dark:border-gray-700 rounded-lg ${
          fillHeight ? 'overflow-auto flex-1 min-h-0' : 'overflow-x-auto'
        }`}
      >
        <table className="w-full text-sm">
          <thead
            className={`bg-gray-50 dark:bg-gray-800 text-gray-500 ${
              fillHeight ? 'sticky top-0 z-10' : ''
            }`}
          >
            <tr>
              {columns.map((col) => {
                const sortable = col.sortable !== false
                const active = sortBy === col.key
                return (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col)}
                    onKeyDown={(e) => {
                      if (sortable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault()
                        handleSort(col)
                      }
                    }}
                    tabIndex={sortable ? 0 : undefined}
                    aria-sort={
                      active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    className={`px-3 py-2 font-medium ${
                      sortable
                        ? 'cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
                        : ''
                    } ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                  >
                    <span
                      className={`inline-flex items-center gap-1 ${
                        col.align === 'right' ? 'flex-row-reverse' : ''
                      }`}
                    >
                      {col.label} {sortable && <SortIcon active={active} order={sortOrder} />}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-6 text-center text-gray-500">
                  {emptyText}
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onRowClick(row)
                          }
                        }
                      : undefined
                  }
                  // Keep the native `row` role (don't override with `button`, which would drop the
                  // row out of the table's a11y tree); just make it focusable and Enter/Space-activatable.
                  tabIndex={onRowClick ? 0 : undefined}
                  className={`border-t border-gray-100 dark:border-gray-800 ${
                    onRowClick
                      ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500'
                      : ''
                  }`}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-3 py-2 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}
                    >
                      {col.render ? col.render(row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-gray-400 shrink-0">
        <span>
          {total === 0
            ? 'No results'
            : `Showing ${start + 1}–${Math.min(start + pageSize, total)} of ${total}`}
        </span>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span>
              Page {safePage} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

KnowledgeNodeTable.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      align: PropTypes.string,
      sortable: PropTypes.bool,
      render: PropTypes.func,
      sortValue: PropTypes.func
    })
  ).isRequired,
  onRowClick: PropTypes.func,
  rowKey: PropTypes.func.isRequired,
  searchKeys: PropTypes.arrayOf(PropTypes.string),
  storageKey: PropTypes.string,
  pageSize: PropTypes.number,
  searchPlaceholder: PropTypes.string,
  emptyText: PropTypes.string,
  // When true, the table fills its parent's height and scrolls internally (sticky header).
  fillHeight: PropTypes.bool
}
